<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

type ToastAction = "increment" | "decrement" | "delete";

type ToastOperation = {
  action: ToastAction;
  timeRecord: string;
}

const { toastOperation, toastSmall = false } = defineProps<{
  toastOperation: ToastOperation;
  toastSmall?: boolean;
}>();

const { t } = useI18n();

const toastList = ref<ToastOperation[]>([]);

const addToast = async (operation: ToastOperation) => {
  toastList.value.splice(0, 0, operation);
  setTimeout(() => {
    toastList.value = toastList.value.filter(i => i !== operation);
  }, 4000);
}

const removeToast = (item: ToastOperation) => {
  toastList.value = toastList.value.filter(i => i !== item);
}

const toast = (action: ToastAction) => {
  switch (action) {
    case 'decrement': {
      return { message: t('item_decrement'), theme: 'danger', adjust: 'decrement' }
    }
    case 'increment': {
      return { message: t('item_increment'), theme: 'success', adjust: 'increment' }
    }
    case 'delete': {
      return { message: t('item_removed'), theme: 'danger', adjust: 'decrement' }
    }
    default: {
      return {}
    }
  }
};

watch(
  () => toastOperation,
  () => {
    addToast(toastOperation)
  },
)
</script>

<template>
  <Transition name="show-toast-list">
    <div
      v-if="toastList.length > 0"
      class="toasts-wrap lg:px-3 absolute top-0 left-0 w-100"
      :class="{'event-none': toastList.length === 0}"
    >
      <div
        class="list-unstyled toasts-wrap-inner relative w-100"
        :class="{ 'toasts-wrap-small': toastSmall }"
      >
        <TransitionGroup
          name="fade"
          tag=""
          class="list-unstyled"
        >
          <li
            v-for="item in toastList"
            :key="JSON.stringify(item)"
            class="toast-item w-100"
            :class="['opened']"
          >
            <div
              v-if="item"
              class="item-box text-white rounded-lg py-1 px-3 flex items-center"
              :class="[`bg-${toast(item.action)?.theme}`]"
              :style="{ transform: `translateX(0%)` }"
            >
              <div class="relative mr-3">
                <i class="mi-cart icon-24" />
                <span
                  class="absolute adjustment-icon top-0 right-0 bg-white rounded-circle mr-n1 mt-n1"
                >
                  <i
                    v-if="toast(item.action).adjust === 'increment'"
                    class="mi-plus icon-12 text-success block"
                  />
                  <i
                    v-else
                    class="mi-minus icon-12 text-danger block"
                  />
                </span>
              </div>
              {{ toast(item.action).message }}
              <button
                class="btn text-white p-2 rounded-circle ml-auto"
                :class="[`btn-${toast(item.action).theme}`]"
                @click="removeToast(item)"
              >
                <i class="mi-times icon-16" />
              </button>
            </div>
          </li>
        </TransitionGroup>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss">
.show-toast-list-enter-active,
.show-toast-list-leave-active {
  transition: opacity 0.5s ease;
}

.show-toast-list-enter-from,
.show-toast-list-leave-to {
  opacity: 0;
}

.toasts-wrap {
  z-index: 100;
  
  &-inner {
    height: 150px;
    overflow: hidden;
    -webkit-mask-image: linear-gradient(#000000 80%, #00000000 95%);
    mask-image: linear-gradient(#000000 80%, #00000000 95%);
  }
  &-small {
    @media screen and (max-width: 992px) {
      height: 64px;
    }
  }

  .toast-item {
    margin-top: 0px;
    margin-bottom: 6px;

    .item-box {
      position: relative;
      transform: translateX(-30%);
      transition: .5s transform .5s;

      .adjustment-icon {
        padding: 2px;
      }
    }
  }
  .fade-move,
  .fade-enter-active,
  .fade-leave-active {
    transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: scaleY(0.01) translate(30px, 0);
  }
  .fade-leave-active {
    position: absolute;
  }
}
</style>

<i18n>
  {
    "cs": {
    "item_decrement": "1 kus odebrán z košíku",
    "item_increment": "1 kus přidán do košíku",
    "item_removed": "Produkt odebrán z košíku"
},
    "sk": {
    "item_decrement": "1 kus odobraný z košíka",
    "item_increment": "1 kus pridaný do košíka",
    "item_removed": "Produkt odobraný z košíka"
},
    "pl": {
    "item_decrement": "Usunięto 1 sztukę z koszyka",
    "item_increment": "Dodano 1 sztukę do koszyka",
    "item_removed": "Produkt usunięty z koszyka"
},
    "hu": {
    "item_decrement": "1 darab kivéve a kosárból",
    "item_increment": "1 darab hozzáadva a kosárhoz",
    "item_removed": "Termék eltávolítva a kosárból"
},
    "de": {
    "item_decrement": "1 Stück aus dem Warenkorb entfernt",
    "item_increment": "1 Stück aus dem Warenkorb hinzugefügt",
    "item_removed": "Produkt aus dem Warenkorb entfernt"
},
    "at": {
    "item_decrement": "1 Stück aus dem Warenkorb entfernt",
    "item_increment": "1 Stück aus dem Warenkorb hinzugefügt",
    "item_removed": "Produkt aus dem Warenkorb entfernt"
},
    "ro": {
    "item_decrement": "1 bucată scoasă din coș",
    "item_increment": "1 bucată adăugată în coș",
    "item_removed": "1 bucată scoasă din coș"
},
    "fr": {
    "item_decrement": "1 pièce retirée du panier",
    "item_increment": "1 pièce ajoutée au panier",
    "item_removed": "Produit retiré du panier"
},
    "it": {
    "item_decrement": "1 pezzo rimosso dal carrello",
    "item_increment": "1 pezzo aggiunto al carrello",
    "item_removed": "Prodotto rimosso dal carrello"
}
  }
</i18n>